import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Input from './Input';
import Select from './Select';
import Button from './Button';

const FilterPanel = ({
  filters = {},
  onFiltersChange,
  schema = [],
  isCollapsed = false,
  onToggleCollapse,
  showClearAll = true,
  className = ''
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchDebounce, setSearchDebounce] = useState(null);

  // Debounced search handling
  useEffect(() => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const timeout = setTimeout(() => {
      onFiltersChange?.(localFilters);
    }, 300);
    
    setSearchDebounce(timeout);

    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [localFilters]);

  const handleFilterChange = (key, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearAll = () => {
    const clearedFilters = {};
    schema?.forEach(field => {
      if (field?.type === 'search') {
        clearedFilters[field.key] = '';
      } else if (field?.type === 'select') {
        clearedFilters[field.key] = field?.multiple ? [] : '';
      } else if (field?.type === 'dateRange') {
        clearedFilters[field.key] = { from: '', to: '' };
      } else if (field?.type === 'date') {
        clearedFilters[field.key] = '';
      } else {
        clearedFilters[field.key] = '';
      }
    });
    setLocalFilters(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(localFilters)?.filter(value => {
      if (Array.isArray(value)) return value?.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.values(value)?.some(v => v !== '' && v !== null);
      }
      return value !== '' && value !== null && value !== undefined;
    })?.length;
  };

  const renderFilterField = (field) => {
    const value = localFilters?.[field?.key] || '';

    switch (field?.type) {
      case 'search':
        return (
          <Input
            key={field?.key}
            type="search"
            placeholder={field?.placeholder || `Search ${field?.label?.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleFilterChange(field?.key, e?.target?.value)}
            className="min-w-[200px]"
          />
        );

      case 'select':
        return (
          <Select
            key={field?.key}
            label={field?.label}
            options={field?.options || []}
            value={value}
            onChange={(newValue) => handleFilterChange(field?.key, newValue)}
            multiple={field?.multiple}
            searchable={field?.searchable}
            clearable={field?.clearable}
            placeholder={field?.placeholder}
            className="min-w-[160px]"
          />
        );

      case 'dateRange':
        return (
          <div key={field?.key} className="flex items-center space-x-2">
            <Input
              type="date"
              label="From"
              value={value?.from || ''}
              onChange={(e) => handleFilterChange(field?.key, { ...value, from: e?.target?.value })}
              className="w-36"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              label="To"
              value={value?.to || ''}
              onChange={(e) => handleFilterChange(field?.key, { ...value, to: e?.target?.value })}
              className="w-36"
            />
          </div>
        );

      case 'date':
        return (
          <Input
            key={field?.key}
            type="date"
            label={field?.label}
            value={value}
            onChange={(e) => handleFilterChange(field?.key, e?.target?.value)}
            className="w-40"
          />
        );

      case 'number':
        return (
          <Input
            key={field?.key}
            type="number"
            label={field?.label}
            placeholder={field?.placeholder}
            value={value}
            onChange={(e) => handleFilterChange(field?.key, e?.target?.value)}
            min={field?.min}
            max={field?.max}
            className="w-32"
          />
        );

      default:
        return (
          <Input
            key={field?.key}
            type="text"
            label={field?.label}
            placeholder={field?.placeholder}
            value={value}
            onChange={(e) => handleFilterChange(field?.key, e?.target?.value)}
            className="min-w-[160px]"
          />
        );
    }
  };

  const activeCount = getActiveFilterCount();

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <button
          onClick={onToggleCollapse}
          className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-smooth"
        >
          <Icon 
            name={isCollapsed ? "ChevronRight" : "ChevronDown"} 
            size={16} 
          />
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full">
              {activeCount}
            </span>
          )}
        </button>

        {!isCollapsed && showClearAll && activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            iconName="X"
            iconPosition="left"
          >
            Clear All
          </Button>
        )}
      </div>
      {/* Filter Content */}
      {!isCollapsed && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {schema?.map(renderFilterField)}
          </div>

          {/* Quick Actions */}
          {schema?.some(field => field?.quickActions) && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex flex-wrap gap-2">
                {schema?.filter(field => field?.quickActions)?.flatMap(field => field?.quickActions)?.map((action, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (action?.filters) {
                          setLocalFilters(prev => ({ ...prev, ...action?.filters }));
                        }
                      }}
                    >
                      {action?.label}
                    </Button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
